import React, { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { isEmpty } from 'lodash';
import { Stack } from '@mui/material';
import { selectCard } from 'js/actions';
import TagsView from 'js/components/elements/tags/TagsView';
import {
  ReusableTransformDetailHeader,
  useGetDataToReusableTransformDetailHeader,
} from 'js/components/reusableTransforms/cards/ReusableTransformDetailHeader';
import ReusableTxDetailActionsContainer from 'js/components/reusableTransforms/cards/ReusableTxDetailActionsContainer';
import { SearchFilterViewsType } from 'js/constants/visibilityFilters';
import { isOwner, ownershipLabel, shouldDisplayOwnershipLabel } from 'js/lib/ACLUtils';
import DataCardList from 'new/elements/DataCardList/DataCardList';
import { TransformType } from 'new/components/ReusableTransform/types';
import { ChipWithCopy } from 'new/elements/ChipWithCopy';
import DataCard, { DataCardCell, NameDescCell } from 'new/elements/DataCard';
import Typography from 'new/elements/TypographySupport/TypographySupport';
import { AvailableFilters } from '../MacroInput/types/AvailableFilters';
import useAppDispatch from 'js/hooks/useAppDispatch';
import { DetailsIconButton } from 'new/elements/Button';

import s from './ReusableTransformsTable.module.scss';

type Props = {
  urlId?: string;
  data: any[];
  isLoading: boolean;
  availableFilterTypes: string[];
  view: SearchFilterViewsType;
  detailViewTabs: any[];
  loadData: (page: number, sortCriteria: string, availableFilters: AvailableFilters) => void;
  onEditButtonClicked: (tx: string) => void;
  noContentView: React.ReactNode;
};

const makeRowRenderer =
  ({ onEditButtonClicked, handleCardSelected, getData, disableOutputType = false }) =>
  (index, item) => {
    const { name, description, createdAt, cardData, readOnly, aclQueryMode, owner, isManaged } =
      getData(index);
    const readOnlyLabel = cardData.public
      ? 'PUBLIC'
      : shouldDisplayOwnershipLabel(aclQueryMode)
        ? ownershipLabel(cardData)
        : null;

    const onClickCard = () => handleCardSelected(cardData);

    return (
      <DataCard
        key={item.id || index}
        onClick={onClickCard}
        rowsSlots={[
          <>
            <NameDescCell name={name} description={description} />
            {readOnlyLabel && (
              <DataCardCell className={s.dataCardCellPublicLabelContainer}>
                <Typography variant="caption">{readOnlyLabel}</Typography>
                {!isOwner(cardData) && !cardData.public && (
                  <Typography variant="body3">
                    <b>OWNED BY: </b> {owner.email}
                  </Typography>
                )}
              </DataCardCell>
            )}
            <DataCardCell rightCTAsCell>
              <DetailsIconButton onClick={onClickCard} />
              <ReusableTxDetailActionsContainer
                data={cardData}
                openDetailsModal={onClickCard}
                onEditButtonClicked={onEditButtonClicked}
                disableEditing={false}
                disableEditMessage={null}
                readOnly={readOnly}
                isManaged={isManaged}
              />
            </DataCardCell>
          </>,
          !isEmpty(cardData.tags) && (
            <DataCardCell>
              <TagsView data={cardData} />
            </DataCardCell>
          ),
          <DataCardCell multiCol>
            <Stack spacing="8px" direction="row" alignItems="flex-end">
              <ChipWithCopy tooltipTitle="Copy Transform ID" toCopy={cardData.id}>
                ID {cardData.id}
              </ChipWithCopy>
              <Typography variant="body3">
                <b>CREATED ON:</b> {createdAt}
              </Typography>
            </Stack>
            {disableOutputType ? null : (
              <Typography
                variant="overline"
                color={
                  cardData.output_type === TransformType.ATTRIBUTE ? 'primary.main' : 'primary.dark'
                }
              >
                {cardData.output_type === TransformType.ATTRIBUTE
                  ? 'Attribute Transform'
                  : 'Record Transform'}
              </Typography>
            )}
          </DataCardCell>,
        ].filter(Boolean)}
      />
    );
  };

const ReusableTransformsTable = ({
  onEditButtonClicked,
  availableFilterTypes,
  view,
  urlId,
  isLoading,
  data,
  detailViewTabs,
  loadData,
  noContentView,
}: Props) => {
  const dispatch = useAppDispatch();
  const listing = useSelector((state: any) => state.app.listing);
  const { selectedCardId, aclQueryMode } = listing;

  const handleCardSelected = useCallback((row) => {
    dispatch(selectCard(row.id));
  }, []);

  useEffect(() => {
    if (urlId) {
      handleCardSelected({ id: urlId });
    }
  }, []);

  const getData = useGetDataToReusableTransformDetailHeader(data, aclQueryMode);

  return (
    <DataCardList
      data={data}
      view={view}
      availableFilterTypes={availableFilterTypes}
      detailViewTabs={detailViewTabs}
      urlId={urlId}
      loading={isLoading}
      detailHeaderContent={
        <ReusableTransformDetailHeader
          {...getData(data.findIndex((item: any) => item.id === selectedCardId))}
          onEditButtonClicked={onEditButtonClicked}
        />
      }
      rowRenderer={makeRowRenderer({
        onEditButtonClicked,
        handleCardSelected,
        getData,
        disableOutputType: true,
      })}
      noContentView={noContentView}
      loadData={loadData}
    />
  );
};

export default ReusableTransformsTable;
